import React, { useState, useEffect } from 'react';
import { withStyles, Button } from '@material-ui/core';
import { AgGrid } from '../../../components/AgGrid';
import classnames from 'classnames';
import { addMonths, startOfMonth, addDays } from 'date-fns';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Autocomplete } from '../../../components/Autocomplete';

// SERVICES
import {
  getClientInvoicesReport,
  confirmClientInvoices,
  clientInvoiceReportExists,
  getExistingClientInvoicesReport,
} from '../../../services';
import { getInitialReportMonth, monthsOptions } from '../../../helpers/constants/months';

const styles = theme => ({
  inputForm: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 20,
  },
});

export const ClientAccounting = withStyles(styles)(props => {
  const { classes, territoryBased } = props;

  const [gridData, setGridData] = useState(null);
  const [showGrid, setShowGrid] = useState(false);
  const [reportAlreadyExists, setReportAlreadyExists] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState(false);

  const [reportMonth, setReportMonth] = useState(getInitialReportMonth);
  const [endDay, setEndDay] = useState(null);

  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [reportIsConfirmed, setReportIsConfirmed] = useState(null);

  const gridColDefs = [
    { headerName: 'Client', field: 'client', width: 500 },
    { headerName: '# of Orders', field: 'orderLineCount' },
    { headerName: 'Taxable Amount ($)', field: 'taxableAmountTotal' },
    { headerName: 'Tax Total ($)', field: 'taxTotal' },
    { headerName: 'Invoice Total ($)', field: 'orderLineTotal' },
  ];

  const getClientInvoicesReportAsync = async () => {
    setGridData(null);
    // const startDate = new Date(start).toUTCString();
    // const endDate = new Date(end).toUTCString();

    try {
      setIsGeneratingReport(true);
      const res = await getClientInvoicesReport(endDay, reportMonth.label, addDays(endDay, 30));
      setIsGeneratingReport(false);

      if (res.results.length) {
        setShowGrid(true);
        return setGridData(res.results);
      }

      setNoDataMessage(true);
    } catch (error) {
      console.log(error);
    }
  };

  const sendConfirmation = async (confirm = false) => {
    try {
      setReportIsConfirmed(confirm);
      setIsSendingConfirmation(true);
      setShowGrid(false);
      const res = await confirmClientInvoices(confirm);
      setIsSendingConfirmation(false);
      setReportIsConfirmed(null);
      setGridData(null);
      setReportAlreadyExists(false);
      setNoDataMessage(false);
    } catch (error) {
      console.log(error);
    }
  };

  // const generateInvoice = () => {
  //   setShowGrid(true);
  //   getClientInvoicesReportAsync();
  // };

  const getExistingClientInvoicesReportAsync = async () => {
    try {
      const res = await getClientInvoicesReportAsync();
      // const res = await getExistingClientInvoicesReport();
      // setGridData(res.results);
      // setShowGrid(true);
      // setReportAlreadyExists(false);
      // setNoDataMessage(true);
    } catch (error) {
      console.log(error);
    }
  };

  const goToHistory = () => {
    window.location = window.location.href + '/history';
  };

  useEffect(() => {
    const clientInvoiceReportExistsAsync = async () => {
      try {
        const res = await clientInvoiceReportExists();
        setReportAlreadyExists(res);
      } catch (error) {
        console.log(error);
      }
    };
    // check if a report is already in progress
    clientInvoiceReportExistsAsync();
  }, []);

  // if the report month changes, reset the start and end dates for that month
  useEffect(() => {
    const { year, monthIndex } = reportMonth;
    const reportMonthDate = addMonths(new Date(year, monthIndex), 1);

    // set the end date as the first day of the next month at midnight
    const end = startOfMonth(reportMonthDate);
    setEndDay(end);
  }, [reportMonth]);

  useEffect(() => {
    setReportMonth(getInitialReportMonth);
  }, [monthsOptions]);

  return (
    // <div className={classes.reportWrapper}>
    <div className={classnames('flex-1', 'col-center-center', 'align-stretch', 'card', 'border-left-green')}>
      <div className={classes.inputForm}>
        {/* show the month that the report is pulled for */}
        <div className={classnames('row-start-center')}>
          <div>Choose a month to run:</div>
          <div style={{ minWidth: 300, marginLeft: 20, marginBottom: 5 }}>
            <Autocomplete disabled={showGrid} options={monthsOptions} value={reportMonth} onChange={setReportMonth} />
          </div>
        </div>

        {!showGrid && !reportAlreadyExists && (
          <div>
            <Button
              variant="contained"
              style={{ backgroundColor: 'var(--jmi-blue)', color: 'white', marginRight: 20 }}
              onClick={goToHistory}
              disabled={isGeneratingReport || isSendingConfirmation}
            >
              View History
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#4CAF50', color: 'white' }}
              onClick={getClientInvoicesReportAsync}
              disabled={isGeneratingReport || isSendingConfirmation}
            >
              Run Invoicing
            </Button>
          </div>
        )}
        {showGrid && gridData && (
          <div className={classnames('flex-1', 'row-end-center')}>
            <Button
              variant="contained"
              style={{ backgroundColor: '#F44336', color: 'white', marginRight: 20 }}
              onClick={() => sendConfirmation(false)}
            >
              Discard Invoicing
            </Button>

            <Button
              variant="contained"
              style={{ backgroundColor: 'var(--jmi-blue)', color: 'white' }}
              onClick={() => sendConfirmation(true)}
            >
              Complete Invoicing
            </Button>
          </div>
        )}
      </div>

      <div className={classnames('flex-1', 'col-center-center', 'align-stretch')} style={{ background: '#ccc' }}>
        {!showGrid && (
          <div className={'col-center-center'}>
            {/* shows a message whenever the report is being generated or accepted/rejected */}
            {isGeneratingReport && (
              <div style={{ marginTop: 20, fontSize: 24 }}>
                <div style={{ marginBottom: 20 }}>Please wait while your report is generated</div>
                <LinearProgress />
              </div>
            )}
            {isSendingConfirmation && (
              <div style={{ marginTop: 20, fontSize: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  Please wait while your report is {reportIsConfirmed ? 'confirmed and exported' : 'discarded'}.
                  {reportIsConfirmed && (
                    <div>
                      {' '}
                      The PDF generation can take a few minutes... You can wait until it finishes or leave this page and
                      continue with other work while the server finishes generating all of the content.
                    </div>
                  )}
                </div>
                <LinearProgress />
              </div>
            )}

            {/* shows messages regarding the data after data has been received */}
            {!isGeneratingReport && !isSendingConfirmation && (
              <div className={classnames('flex-1', 'align-stretch', 'col-center-center')}>
                {!reportAlreadyExists && !noDataMessage && (
                  <div style={{ marginTop: 20, fontSize: 24 }}>Please run invoicing for the current period</div>
                )}

                {!reportAlreadyExists && noDataMessage && (
                  <div style={{ marginTop: 20, fontSize: 24 }}>
                    No new orders were found. No new invoices were created. Please check back later
                  </div>
                )}

                {reportAlreadyExists && (
                  <div style={{ width: '80%' }}>
                    <div style={{ marginBottom: 30, fontSize: 24 }}>
                      An invoice report already exists... Either discard and regenerate the report or view it to confirm
                      the details
                    </div>
                    <div>
                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: '#F44336',
                          color: 'white',
                          marginRight: 20,
                        }}
                        onClick={() => sendConfirmation(false)}
                      >
                        Discard Report
                      </Button>

                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: 'var(--jmi-blue)',
                          color: 'white',
                          marginLeft: 20,
                        }}
                        onClick={() => getExistingClientInvoicesReportAsync()}
                      >
                        View Report
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* shows the grid when data is received */}
        {showGrid && (
          <AgGrid gridData={gridData} columnDefs={gridColDefs} redirectOnClick={false} editOnClick={false} />
        )}
      </div>
    </div>
  );
});
